/**
 * @file Breadcrumbs.js — Fil d'Ariane contextuel basé sur l'URL courante.
 * Affiche le chemin de navigation sous la barre de sous-navigation
 * pour aider l'utilisateur à se repérer dans la hiérarchie de l'application (R-0089).
 */
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../contexts/AppContext';
import { getDefaultRouteForView, getSectionViewForPath, getVisibleSegments, normalizeAppPath } from '../lib/routes';

/**
 * Table de correspondance entre les segments d'URL et les libellés affichés.
 * Les segments dynamiques (UUID) ne sont pas dans cette table et seront
 * affichés comme « Détail » par défaut.
 */
const SEGMENT_LABELS = {
  d: 'nav.dashboard',
  c: 'nav.circles',
  p: 'nav.profile',
  g: 'profile.twofa_title',
  e: 'nav.events',
  o: 'nav.overview',
  m: 'nav.members',
  i: 'nav.invites',
  s: 'admin.signups_title',
  u: 'site_user_detail.title',
  x: 'site_database.export_button',
};

/**
 * Vérifie si un segment ressemble à un UUID (format 8-4-4-4-12 hexadécimal).
 * @param {string} segment - Segment de l'URL.
 * @returns {boolean}
 */
function isUuid(segment) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
}

function isIdentifierSegment(segment, previousSegment) {
  return ['c', 'e', 'u'].includes(previousSegment) || isUuid(segment);
}

/**
 * Composant fil d'Ariane.
 * Construit automatiquement les liens à partir du chemin courant.
 * Masqué sur la page d'accueil et le dashboard (niveau racine).
 * @returns {React.ReactElement|null}
 */
export default function Breadcrumbs() {
  const { t } = useTranslation();
  const { activeCircle } = useApp();
  const location = useLocation();
  const normalizedPath = normalizeAppPath(location.pathname) || location.pathname;
  const segments = getVisibleSegments(normalizedPath);
  const fullSegments = normalizedPath.split('/').filter(Boolean);
  const prefixSegments = fullSegments.slice(0, fullSegments.length - segments.length);
  const routeView = getSectionViewForPath(normalizedPath) || 'user';
  const homeTarget = getDefaultRouteForView(routeView);

  /* Ne pas afficher sur les pages de niveau racine */
  if (segments.length <= 1) return null;

  /* Construire les éléments du fil d'Ariane */
  const crumbs = segments.map((segment, index) => {
    const path = '/' + prefixSegments.concat(segments.slice(0, index + 1)).join('/');
    const label = SEGMENT_LABELS[segment]
      ? t(SEGMENT_LABELS[segment])
      : (isIdentifierSegment(segment, segments[index - 1]) ? t('breadcrumbs.detail') : segment);
    const isLast = index === segments.length - 1;

    return { path, label, isLast };
  });

  return (
    <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" aria-label={t('breadcrumbs.aria')} data-testid="breadcrumbs">
      <div className="rounded-sm border border-border/70 bg-muted/30 px-3 py-2 flex flex-wrap items-center gap-3">
        <ol className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
          <li>
            <Link to={homeTarget} className="hover:text-foreground transition-colors">
              <Home className="h-3.5 w-3.5" />
            </Link>
          </li>

          {crumbs.map(({ path, label, isLast }) => (
            <li key={path} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3" />
              {isLast ? (
                <span className="text-foreground font-medium">{label}</span>
              ) : (
                <Link to={path} className="hover:text-foreground transition-colors">
                  {label}
                </Link>
              )}
            </li>
          ))}
        </ol>

        {activeCircle && (
          <div className="ml-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="breadcrumbs-active-circle">
            <span>{t('breadcrumbs.active_circle')}</span>
            <span className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-semibold tracking-[0.12em] text-foreground">
              {activeCircle.name}
            </span>
          </div>
        )}
      </div>
    </nav>
  );
}
